import React from 'react'
import classNames from 'classnames'
import { capitalize, omit, isEmpty } from 'libraries/lodash'
import url from 'libraries/front-end-core/core/url'
import { LEGISLATION } from 'constants/controllers'
import { toJson } from 'libraries/front-end-core/helpers/xml'
import ContentParagraphContainer from 'redux/containers/components/legislation-reference/ContentParagraphContainer'
import { useSelector } from 'react-redux'
import selectAolName from 'redux/selectors/selectAolName'

const ItemWrapper = ({
  root,
  nodes,
  linkable,
  depth = 0,
  context = []
}) => {
  const rootNode = nodes[root]
  const { id, number, level, levelName, isLeaf, title, content } = rootNode
  let children

  if (isLeaf) {
    children = <ContentWrapper content={content} />
  } else {
    children = <ChildrenWrapper {...rootNode} {...{ nodes, depth }} />
  }

  return (
    <div className='lr-treenode'>
      <ItemTitle {...{ level, levelName, number, id, title, depth, context, isLeaf, linkable }} />
      {children}
    </div>
  )
}

const ItemTitle = ({ depth, id, isLeaf, level, linkable, number, title, context }) => (
  <div
    className={classNames({
      'lr-treeheader': true,
      'lr-leaf': isLeaf,
      'lr-container': !isLeaf,
      [`depth-${depth + 1}`]: true
    })}
  >
    <ItemTitleElement {...{ id, isLeaf, linkable, title, number, levelName: level }} />
    <ItemBreadcrumbs {...{ id, linkable, context }} />
  </div>
)

const ItemTitleElement = ({ id, isLeaf, levelName, number, title }) => {
  const aol = useSelector(selectAolName)

  return (
    <a
      className={classNames({ 'fc-question': isLeaf, 'lr-treetitle': !isLeaf })}
      href={url(LEGISLATION, { aol, path: id })}
      data-event-category='Legislation Reference'
      data-event-action='Breadcrumb'
      data-event-label={title}
    >
      <ItemNumber {...{ levelName, number }} />
      {title}
    </a>
  )
}

const ItemNumber = ({ levelName, number }) => !number
  ? null
  : (
    <span className='number'>{capitalize(levelName)} {number},&nbsp;</span>
    )

const ItemBreadcrumbs = ({ context, linkable }) => {
  const aol = useSelector(selectAolName)

  if (isEmpty(context)) {
    return null
  }
  // key passed as key= to make it more explicit what key is set (also shuts the linter up)
  return (
    <span className='lr-breadcrumbs'>
      {context.map((item, key) => <ItemBreadcrumb key={key} {...{ aol, linkable, ...item }} />)}
    </span>
  )
}

const ItemBreadcrumb = ({ aol, linkable, id, level, number, title }) => {
  const legUrl = url(LEGISLATION, { aol, path: id })

  return linkable
    ? (
      <span>
        <a
          href={legUrl}
          title={number && `[${capitalize(level)} ${number}]`}
          data-event-category='Legislation Reference'
          data-event-action='Breadcrumb'
          data-event-label={title}
        >
          {title}
        </a>
      </span>
      )
    : (
      <span>
        <a>{title}</a>
      </span>
      )
}

const ContentWrapper = (props) => (
  <div className='fc-qa'>
    <div className='fc-answer-wrap'>
      <div className='fc-answer section-content'>
        {toJson(props.content).map((node, index) => (
          <SectionContent key={index} {...omit(props, 'content')} {...node} />
        ))}
      </div>
    </div>
  </div>
)

const ChildrenWrapper = ({ descendants, nodes, depth }) => (
  <div>
    {descendants.map((root, key) => (
      <ItemWrapper key={key} {...{ root, nodes, depth: depth + 1 }} />
    ))}
  </div>
)

export const SectionContent = (props) => {
  switch (props.name) {
    case 'p':
      return <ContentParagraphContainer {...props} />
    case 'em':
      return <ContentEmphasis {...props} />
    case 'partialnote':
    case 'jurisdictionnote':
    case 'amend':
    case 'title':
    case 'crossheading':
    case 'table':
    case 'tbody':
    case 'tr':
    case 'td':
      return <ContentNote {...props} />
    case 'chapter':
    case 'section':
      return <ContentSections {...props} />
    case 'text':
    default:
      return <ContentText {...props} />
  }
}

export const ContentReference = ({ reference }) => reference
  ? (
    <span className='reference'>{reference}</span>
    )
  : null

const ContentEmphasis = ({ childNodes }) => (
  <em>{childNodes.map((node, index) => <SectionContent key={`${node.content}-${index}`} {...node} />)}</em>
)

const ContentText = ({ content }) => (
  <span>{content}</span>
)

const ContentNote = ({ name, reference, childNodes }) => (
  <div className={`fc-${name}`}>
    <ContentReference reference={reference} />
    {childNodes.map((node, index) => <SectionContent key={index} {...node} />)}
  </div>
)

const ContentSections = ({ name, childNodes, attributes: { ref: reference } }) => (
  <div className={`fc-${name}`}>
    {childNodes.map((node, index) => <SectionContent key={index} reference={reference} {...node} />)}
  </div>
)

export default ItemWrapper
